package com.kinoshazam.screens.person.medium

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.details.medium.DialogContent
import com.kinoshazam.screens.person.PersonFilmographyButton
import com.kinoshazam.screens.person.compact.personSubtitle
import com.kinoshazam.uiKit.components.expandableDescription.ExpandableDescription
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.image.UiKitImage
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.MediumVideoCardWidthCompact
import com.kinoshazam.uiKit.components.videoItem.MediumVideoItem
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldExtraLarge
import com.kinoshazam.uiKit.theme.MediumMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.ErrorView
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.repeat
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.person.PersonMVIAction
import presentation.screens.person.PersonState

@Composable
fun MediumPersonContent(
    state: PersonState,
    onAction: (PersonMVIAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    Scaffold(
        modifier = modifier
            .fillMaxSize()
            .background(UiKitTheme.colors.transparent),
        containerColor = UiKitTheme.colors.transparent,
        content = {
            DialogContent(
                onClose = { onAction(PersonMVIAction.BackClicked) },
                fillMaxHeight = true,
            ) {
                when {
                    state.isLoading -> LoadingScreen()
                    state.isError -> ErrorScreen(onAction)
                    else -> ContentScreen(state, onAction)
                }
            }
        }
    )
}

@Composable
private fun LoadingScreen() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(24.dp),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 2.dp,
        )
    }
}

@Composable
private fun ErrorScreen(onAction: (PersonMVIAction) -> Unit) {
    ErrorView(
        textError = stringResource(Res.string.error),
        textAction = stringResource(Res.string.repeat),
        onAction = { onAction(PersonMVIAction.Reload) }
    )
}

@Composable
private fun ContentScreen(
    state: PersonState,
    onAction: (PersonMVIAction) -> Unit,
) {
    val person = state.person ?: return
    val scrollState = rememberScrollState()

    Box(modifier = Modifier.fillMaxSize()) {
        PersonBackground(person.profilePath)
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(modifier = Modifier.statusBarsPadding().height(72.dp))
                UiKitImage(
                    modifier = Modifier
                        .size(140.dp)
                        .clip(CircleShape),
                    state = ImageUiKitState(
                        contentDescription = person.name,
                        url = person.profilePath,
                        placeholder = painterResource(Res.drawable.movie),
                        placeholderColor = UiKitTheme.colors.color_5,
                        contentScale = ContentScale.Crop,
                    ),
                )
                Spacer(modifier = Modifier.height(16.dp))
                TextLabel(
                    state = TextLabelState(
                        text = person.name.orEmpty(),
                        textStyle = BoldExtraLarge,
                        color = UiKitTheme.colors.color_2,
                    )
                )
                personSubtitle(person.birthday, person.placeOfBirth)?.let { subtitle ->
                    Spacer(modifier = Modifier.height(8.dp))
                    TextLabel(
                        state = TextLabelState(
                            text = subtitle,
                            textStyle = MediumMedium,
                            color = UiKitTheme.colors.color_2,
                        )
                    )
                }
                if (person.filmography.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(32.dp))
                    PersonFilmographyButton(
                        onClick = { onAction(PersonMVIAction.OpenFilmography) },
                    )
                }
            }

            if (!person.biography.isNullOrBlank()) {
                Spacer(modifier = Modifier.height(16.dp))
                ExpandableDescription(
                    text = person.biography.orEmpty(),
                    expanded = state.isFullBiography,
                    onToggle = { onAction(PersonMVIAction.ToggleBiography) },
                )
            }

            if (person.filmography.isNotEmpty()) {
                Spacer(modifier = Modifier.height(24.dp))
                Spacer(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(1.dp)
                        .padding(horizontal = 16.dp)
                        .background(color = UiKitTheme.colors.color_4)
                )
                Spacer(modifier = Modifier.height(16.dp))
                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    contentPadding = PaddingValues(horizontal = 16.dp),
                ) {
                    itemsIndexed(person.filmography) { _, video ->
                        MediumVideoItem(
                            state = VideoState(
                                id = video.id ?: -1,
                                title = video.title.orEmpty(),
                                image = video.imageUrl,
                                placeHolder = painterResource(Res.drawable.movie),
                                placeHolderColor = UiKitTheme.colors.color_5,
                                onAction = { id ->
                                    onAction(
                                        PersonMVIAction.OnVideoClicked(id, video.isTvShow)
                                    )
                                }
                            ),
                            width = MediumVideoCardWidthCompact,
                            modifier = Modifier.padding(bottom = 16.dp),
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(48.dp))
        }
    }
}

@Composable
private fun BoxScope.PersonBackground(profilePath: String?) {
    UiKitImage(
        state = ImageUiKitState(
            url = profilePath,
            contentScale = ContentScale.FillHeight,
        ),
        alignment = Alignment.TopCenter,
        modifier = Modifier.fillMaxSize(),
    )
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        UiKitTheme.colors.color_3.copy(alpha = 0.3f),
                        UiKitTheme.colors.color_3,
                    )
                )
            )
    )
}
